import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";

import { GiftCardPreview } from "../GiftCardPreview/GiftCardPreview";

type GiftCardMultiPreviewProps = {
  cards: {
    imageUrl: string;
    cost: number;
    name: string;
    recipientInformation: string;
    senderInformation: string;
    logoImageUrl: string;
    message?: string;
  }[];
};

export const GiftCardMultiPreview = ({ cards }: GiftCardMultiPreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    const isFirstCard = currentIndex === 0;
    const newIndex = isFirstCard ? cards.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLastCard = currentIndex === cards.length - 1;
    const newIndex = isLastCard ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  // If there are no cards, don't render anything
  if (cards.length === 0) {
    return null;
  }

  // If there's only one card, don't show navigation
  if (cards.length === 1) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-4">
        <div className="flex h-full w-full scale-50 flex-col items-center justify-center gap-4 p-4 transition-all duration-300 xl:scale-75 2xl:scale-100">
          <GiftCardPreview
            preview={{
              image: cards[0]?.imageUrl ?? "",
              valueInCents: cards[0]?.cost ?? 0,
            }}
            recipientName={cards[0]?.recipientInformation ?? ""}
            senderName={cards[0]?.senderInformation ?? ""}
            platformLogo={cards[0]?.logoImageUrl ?? ""}
            isSomeoneElse={
              cards[0]?.recipientInformation !== cards[0]?.senderInformation
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <div className="relative flex h-full w-full scale-50 flex-col items-center justify-center gap-4 p-4 transition-all duration-300 xl:scale-75 2xl:scale-100">
        {/* Gift Card Counter */}
        <div className="text-center">
          <p className="text-body-3 text-secondary">
            Gift Card {currentIndex + 1} of {cards.length}
          </p>
        </div>

        {/* Gift Card with Navigation */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="relative"
            >
              <GiftCardPreview
                preview={{
                  image: cards[currentIndex]?.imageUrl ?? "",
                  valueInCents: cards[currentIndex]?.cost ?? 0,
                }}
                recipientName={cards[currentIndex]?.recipientInformation ?? ""}
                senderName={cards[currentIndex]?.senderInformation ?? ""}
                platformLogo={cards[currentIndex]?.logoImageUrl ?? ""}
                isSomeoneElse={
                  cards[currentIndex]?.recipientInformation !==
                  cards[currentIndex]?.senderInformation
                }
              />
            </motion.div>
          </AnimatePresence>

          {/* Left Chevron - positioned relative to gift card */}
          <button
            onClick={goToPrevious}
            aria-label="Previous gift card"
            className="absolute -left-12 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg transition-colors hover:bg-gray-50"
          >
            <ChevronLeft className="h-5 w-5 text-brand-color" />
          </button>

          {/* Right Chevron - positioned relative to gift card */}
          <button
            onClick={goToNext}
            aria-label="Next gift card"
            className="absolute -right-12 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg transition-colors hover:bg-gray-50"
          >
            <ChevronRight className="h-5 w-5 text-brand-color" />
          </button>
        </div>

        {/* Message Display */}
        {cards[currentIndex]?.message && (
          <div className="max-w-md text-center">
            <p className="text-body-3 italic text-secondary">
              &ldquo;{cards[currentIndex].message}&rdquo;
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
